import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { toast } from "sonner";
import {
  Wand2, RefreshCw, Loader2, Check, X, Search, Filter,
  ChevronDown, ChevronRight, ScrollText, AlertTriangle, Sparkles,
} from "lucide-react";
import {
  adminCopyInventory, adminSuggestCopy, adminApplyCopy,
} from "../lib/copyAdaptApi";
import { formatError } from "../lib/api";

const SOURCE_OPTIONS = [
  { key: "worksheet",          label: "Course Workbook · Worksheets",   hint: "48 worksheets × 2 fields (purpose, outcome)" },
  { key: "workbook_module",    label: "Workbook Modules",               hint: "16 module descriptions" },
  { key: "lms_module",         label: "LMS Modules",                    hint: "The 16 legacy course modules — description, summary, narrative" },
  { key: "course_overview",    label: "Course Overview page",           hint: "Headline / subhead / body of /course" },
  { key: "blueprint_section",  label: "Launch Blueprint sections",      hint: "Section titles, descriptions, and body copy" },
  { key: "dashboard_headline", label: "Dashboard greetings",            hint: "Morning / afternoon / evening headline strings" },
];

const AUDIENCE_OPTIONS = [
  { key: "everyday",  label: "Everyday",  hint: "First-time entrepreneurs, no CPG background" },
  { key: "founder",   label: "Founder",   hint: "Early-stage CPG founders, 6-24 months in" },
  { key: "insider",   label: "Insider",   hint: "Experienced CPG operators, tight industry voice" },
  { key: "general",   label: "General",   hint: "Mixed audience of prospects + current founders" },
];

const STYLE_PRESETS = [
  "Warmer, encouraging", "Punchier, tighter", "No analogies",
  "More concrete, less abstract", "Reduce jargon", "Cut length by 30%",
];

// ── Row component: renders one inventory field with its rewrite workspace ─
function InventoryRow({ item, onApplied }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("founder");
  const [styleDirective, setStyleDirective] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [suggestionMeta, setSuggestionMeta] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const suggest = async () => {
    setBusy(true);
    try {
      const res = await adminSuggestCopy({
        source: item.source, sourceId: item.source_id, field: item.field,
        originalText: item.original_text,
        audience, styleDirective,
      });
      setSuggestion(res.suggested);
      setSuggestionMeta({ model: res.model, audience: res.audience,
                          styleDirective: res.style_directive });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!suggestion.trim()) return;
    setApplying(true);
    try {
      const res = await adminApplyCopy({
        source: item.source, sourceId: item.source_id, field: item.field,
        newText: suggestion,
        audience, styleDirective,
      });
      toast.success(`Applied — ${res.cache_purged} cached rows purged`);
      setApplied(true);
      // Update the visible original text so the row shows the new baseline.
      item.original_text = suggestion;
      onApplied && onApplied(item, suggestion);
      setTimeout(() => setApplied(false), 4000);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="surface overflow-hidden" data-testid={`copy-inventory-row-${item.source_id}-${item.field}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-muted/40 transition"
      >
        <div className="w-7 h-7 rounded-lg bg-shell-accent/10 grid place-items-center shrink-0">
          <Wand2 className="w-3.5 h-3.5 text-shell-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1">
            <span className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold">
              {item.module_number != null ? `M${item.module_number}` : "—"} · {item.field}
            </span>
            <span className="text-sm font-semibold text-foreground truncate">{item.label}</span>
            <span className="ml-auto text-[11px] text-muted-foreground shrink-0">{item.chars} chars</span>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2 leading-snug">
            {item.original_text}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground mt-1 shrink-0 transition ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-shell-border/60 bg-muted/30 px-5 py-5" data-testid={`copy-workspace-${item.source_id}-${item.field}`}>
          {/* Controls */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-semibold">Audience</label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="mt-1 w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm outline-none focus:border-shell-accent"
                data-testid={`copy-audience-${item.source_id}-${item.field}`}
              >
                {AUDIENCE_OPTIONS.map((a) => (
                  <option key={a.key} value={a.key}>{a.label} — {a.hint}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-semibold">Style directive</label>
              <div className="mt-1 flex flex-wrap gap-1.5 mb-2">
                {STYLE_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setStyleDirective((prev) =>
                      prev.includes(p) ? prev : (prev ? `${prev}, ${p}` : p))}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-shell-border/70 hover:border-shell-accent hover:text-shell-accent transition"
                  >
                    + {p}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={styleDirective}
                onChange={(e) => setStyleDirective(e.target.value)}
                placeholder='e.g. "warmer + shorter, no analogies"'
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm outline-none focus:border-shell-accent"
              />
            </div>
          </div>

          {/* Suggest / Regenerate button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={suggest}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-shell-side text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`copy-suggest-${item.source_id}-${item.field}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (suggestion ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
              {suggestion ? "Regenerate suggestion" : "Suggest a rewrite"}
            </button>
            {suggestionMeta && (
              <span className="ml-3 text-[11px] text-muted-foreground">
                {suggestionMeta.model}
              </span>
            )}
          </div>

          {/* Before / After */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-semibold mb-1.5">
                Current (live copy)
              </div>
              <div className="rounded-lg border border-shell-border bg-white p-3.5 text-sm leading-relaxed text-foreground/85 whitespace-pre-line max-h-72 overflow-y-auto">
                {item.original_text}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold">
                  Proposed rewrite
                </div>
                {suggestion && (
                  <span className="text-[11px] text-muted-foreground">
                    {suggestion.length} chars · Δ {suggestion.length - item.original_text.length > 0 ? "+" : ""}
                    {suggestion.length - item.original_text.length}
                  </span>
                )}
              </div>
              <textarea
                rows={10}
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                placeholder={busy ? "Generating…" : 'Click "Suggest a rewrite" to get an LLM proposal. You can edit it before applying.'}
                className="w-full rounded-lg border border-shell-accent/40 bg-white p-3.5 text-sm leading-relaxed outline-none focus:border-shell-accent resize-y"
                data-testid={`copy-suggestion-textarea-${item.source_id}-${item.field}`}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-[11px] text-muted-foreground max-w-lg">
              Applying overwrites the SOURCE copy for every reader.
              An audit trail (last 5 rewrites per doc) is kept automatically.
              The reader-level cache is purged so the new baseline propagates immediately.
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {applied && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold" data-testid={`copy-applied-${item.source_id}-${item.field}`}>
                  <Check className="w-3.5 h-3.5" /> Applied
                </span>
              )}
              <button
                type="button"
                onClick={() => { setSuggestion(""); setSuggestionMeta(null); }}
                disabled={!suggestion || applying}
                className="px-3 py-1.5 rounded-md border border-shell-border text-xs font-medium hover:border-foreground/40 disabled:opacity-40"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={!suggestion.trim() || applying || applied}
                className="btn-primary text-xs disabled:opacity-40"
                data-testid={`copy-apply-${item.source_id}-${item.field}`}
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Apply to source
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function AdminCopyConsole() {
  const [source, setSource] = useState("worksheet");
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");

  useEffect(() => {
    setLoading(true); setInventory(null); setError(null); setFieldFilter("");
    adminCopyInventory(source)
      .then(setInventory)
      .catch((e) => setError(formatError(e)))
      .finally(() => setLoading(false));
  }, [source]);

  const filtered = useMemo(() => {
    const items = inventory?.items || [];
    return items.filter((it) => {
      if (fieldFilter && it.field !== fieldFilter) return false;
      if (q) {
        const hay = `${it.label} ${it.original_text}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [inventory, fieldFilter, q]);

  const sourceMeta = SOURCE_OPTIONS.find((s) => s.key === source);

  return (
    <AppShell title="Copy Rewrite Console">
      <div className="w-full pb-24">
        <PageHero
          eyebrow="Content operations"
          icon={Wand2}
          title={<>Copy <span className="text-grad">Rewrite Console</span></>}
          subtitle="Preview and approve LLM-suggested rewrites of the source-of-truth copy. Applied rewrites become the new baseline for every reader and are audit-logged automatically."
          testid="copy-console-hero"
        />

        {/* Source picker */}
        <section className="surface p-5 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-semibold">
                Source
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="mt-1 w-full rounded-md border border-shell-border bg-white px-3 py-2.5 text-sm outline-none focus:border-shell-accent"
                data-testid="copy-source-picker"
              >
                {SOURCE_OPTIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              {sourceMeta?.hint && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">{sourceMeta.hint}</p>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-semibold">
                Filter within source
              </label>
              <div className="mt-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search label or text…"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-shell-border bg-white text-sm outline-none focus:border-shell-accent"
                    data-testid="copy-search"
                  />
                </div>
                {inventory?.fields?.length > 1 && (
                  <select
                    value={fieldFilter}
                    onChange={(e) => setFieldFilter(e.target.value)}
                    className="px-3 py-2 rounded-md border border-shell-border bg-white text-sm outline-none"
                    data-testid="copy-field-filter"
                  >
                    <option value="">All fields</option>
                    {inventory.fields.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Inventory list */}
        {error && (
          <div className="surface p-6 text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
        {loading && (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && inventory && (
          <>
            <div className="mb-3 flex items-center justify-between text-[11px] uppercase tracking-widest text-muted-foreground/85">
              <span data-testid="copy-inventory-count">{filtered.length} rewritable passage{filtered.length === 1 ? "" : "s"}</span>
              <span>Model: Claude Sonnet 4.6</span>
            </div>
            <div className="space-y-2.5" data-testid="copy-inventory-list">
              {filtered.map((it, i) => (
                <InventoryRow key={`${it.source_id}::${it.field}::${i}`} item={it} />
              ))}
              {filtered.length === 0 && (
                <div className="surface p-10 text-center text-muted-foreground">
                  No rewritable passages match those filters.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
